import { pxToEm } from 'util/pxToEm';
import { Box, Typography } from '@mui/material';
import { Link } from 'components/lib';
import { TextInput, FormErrors, Button, Divider } from 'components/ui';
import { signUpFormSchema, SignUpFormData } from 'forms';
import { useAppForm } from 'hooks';
import React, { ReactElement } from 'react';
import { useRouter } from 'next/router';
import { theme } from 'theme';
import { Routes } from 'services/routes';
import { useAuthApi } from 'services/api';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { RECAPTCHA_SITE_KEY } from 'config/app.config';
import { useSnackbar } from 'notistack';

export const SignUpForm = (): ReactElement => {
  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields: touched, isSubmitting },
  } = useAppForm(signUpFormSchema, {
    mode: 'all',
  });

  const router = useRouter();
  const authApi = useAuthApi();
  const { enqueueSnackbar } = useSnackbar();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const recaptchaSiteKey = RECAPTCHA_SITE_KEY;

  const onSubmit = async (data: SignUpFormData) => {
    // Execute reCAPTCHA v3 if site key is configured
    if (recaptchaSiteKey && executeRecaptcha) {
      try {
        const token = await executeRecaptcha('signup');
        if (token)
          data.captchaToken = token;

      } catch (error) {
        enqueueSnackbar('Failed to execute reCAPTCHA', { variant: 'error' });
      }
    }

    await authApi.startSignUp(data);

    await router.push(
      {
        pathname: Routes.signUpConfirm(),
        query: {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
        },
      },
      Routes.signUpConfirm(),
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box mt={8}>
        <Typography sx={{ fontSize: pxToEm(18) }}>
          Let’s start with your name and email address.
        </Typography>
      </Box>
      <Box mt={16} display="flex">
        <Box flex={1} mr={32}>
          <TextInput
            label="First Name"
            placeholder=""
            hasError={!!touched.firstName && !!errors.firstName}
            {...register('firstName')}
          />
        </Box>
        <Box flex={1}>
          <TextInput
            label="Last Name"
            placeholder=""
            hasError={!!touched.lastName && !!errors.lastName}
            {...register('lastName')}
          />
        </Box>
      </Box>

      <Box mt={24}>
        <TextInput
          type="email"
          label="Email Address"
          hasError={!!touched.email && !!errors.email}
          {...register('email')}
        />
        <FormErrors>{errors.email?.message}</FormErrors>
      </Box>

      <Box mt={30}>
        <Box mb={20}>
          <Typography sx={{ fontSize: pxToEm(12) }}>
            By clicking the next button and proceeding, I agree that I have read
            and accepted the
            <Link
              href={Routes.external.marketing.terms()}
              target="_blank"
              style={{ fontWeight: 700, color: theme.palette.primary.main }}
            >
              {' '}
              Terms of Service{' '}
            </Link>
            and
            <Link
              href={Routes.external.marketing.privacy()}
              target="_blank"
              style={{ fontWeight: 700, color: theme.palette.primary.main }}
            >
              {' '}
              Privacy Policy
            </Link>
            .
          </Typography>
        </Box>
        <Button
          type="submit"
          minWidth="100%"
          variant="vibrant"
          disabled={isSubmitting}
        >
          Next
        </Button>
        <Divider size={1} style={{ marginTop: 32, marginBottom: 24 }} />
      </Box>
    </form>
  );
};
